import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { FC, useState, useEffect } from "react";
import {
    Button,
    TextField,
    IconButton,
    InputAdornment,
    Alert,
    Fade,
} from "@mui/material";
import {
    Visibility,
    VisibilityOff,
} from "@mui/icons-material";

const PasswordChangeDialog: FC<{
    open: boolean;
    onClose: () => void;
    onSave: (newPassword: string) => void;
}> = ({ open, onClose, onSave }) => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // フォームリセット
    const resetForm = () => {
        setNewPassword("");
        setConfirmPassword("");
        setError(null);
        setSuccess(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
    };

    // ダイアログが開かれた時にフォームをリセット
    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open]);

    const handleSave = async () => {
        setError(null);

        // バリデーション
        if (newPassword.length < 8) {
            setError("パスワードは8文字以上で入力してください");
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("新しいパスワードと確認用パスワードが一致しません");
            return;
        }

        try {
            await onSave(newPassword);
            setSuccess(true);
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (err) {
            setError("パスワードの変更に失敗しました");
        }
    };

    const handleClose = () => {
        if (!success) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>パスワード変更</DialogTitle>
            <DialogContent>
                {success && (
                    <Fade in={success}>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            パスワードが正常に変更されました
                        </Alert>
                    </Fade>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    margin="normal"
                    label="新しいパスワード"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={success}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    edge="end"
                                    disabled={success}
                                >
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />

                <TextField
                    fullWidth
                    margin="normal"
                    label="新しいパスワード（確認）"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={success}
                    error={confirmPassword !== "" && newPassword !== confirmPassword}
                    helperText={
                        confirmPassword !== "" && newPassword !== confirmPassword
                            ? "パスワードが一致しません"
                            : ""
                    }
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                    disabled={success}
                                >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={success}>
                    キャンセル
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    color="primary"
                    disabled={success || !newPassword || !confirmPassword}
                >
                    変更する
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PasswordChangeDialog;